// react imports
import {
  View,
  ViewStyle,
  TextStyle,
  TextInput,
  ImageStyle,
  Image,
  Pressable,
} from 'react-native';
import React, {useCallback, useEffect, useState} from 'react';

// routing related imports
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';

// ui-kits, colors, spacing, etc
import {Button, Table, StatusTag, Icon, Text} from 'ui-kits';
import {spacing, corner_radius} from 'theme/Units';
import colors from 'theme/Colors';

import {
  projectList,
  projectStatus,
  projectFailure,
  projectTotalPages,
  projectSuccess,
} from 'redux/selectors/projectSelector';

import {
  Project,
  getProjectsRequest,
  resetMessage,
  setCurrentSelectedProject,
  setProjectName,
} from 'redux/slices/projectSlice';

import {getOrgID, getUserID} from 'redux/selectors/authSelector';

// utils
import {getFormattedDate} from 'utils/getFormattedDate';
import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';

// constants
import {ERROR_KEY, SUCCESS_KEY} from 'constants/keys';

// hooks
import {useDispatch, useSelector} from 'react-redux';
import {useAuthAccess, useDebounceState, useToast} from 'hooks';
import {SimpleDropdown} from 'ui-kits/Dropdown/SimpleDropdown';
import {projectStatus as projectStatusEnum} from 'constants/enums';

type Column = {
  key: keyof Project;
  label: JSX.Element | string;
  minWidth?: number;
  renderCell?: (cellData: any) => JSX.Element | null;
};

type SortOrder = 'asc' | 'desc' | null;

interface SortState {
  name?: SortOrder;
  id?: SortOrder;
}

export function ViewProject({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch();
  const org_id = useSelector(getOrgID);
  const user_id = useSelector(getUserID);

  const {hasWriteAccess} = useAuthAccess();
  const projects = useSelector(projectList);
  const isLoading = useSelector(projectStatus);
  const getProjectError = useSelector(projectFailure);
  const getProjectSuccess = useSelector(projectSuccess);
  const totalPages = useSelector(projectTotalPages);
  const [sort, setSort] = useState<SortState>({id:'desc'});
  const [searchText, setSearchText] = useDebounceState<string>('', 500);

  //to filter based on project status
  const [filterState, setFilterState] = useState<number>();
  const [statusDropDownOpen, setStatusDropDownOpen] = useState<boolean>(false);

  const {showToasts} = useToast();

  const fetchProjects = useCallback(() => {
    const filter = [];
    if (filterState) {
      filter.push({status: filterState});
    }
    const params: any = {
      user_id,
      org_id,
      page: currentPage,
      search_text: searchText,
      sort: decodeURIComponent(JSON.stringify([sort])),
      filter: decodeURIComponent(JSON.stringify(filter)),
    };

    dispatch(getProjectsRequest(params));
  }, [dispatch, org_id, user_id, currentPage,sort, searchText, filterState]);

  // api fetch for project list
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // show error only when they come
  useEffect(() => {
    if (getProjectError) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(getProjectError as ErrorCodes),
        'error',
      );
      dispatch(resetMessage());
    }
  }, [getProjectError, dispatch, showToasts]);

  // show error only when they come
  useEffect(() => {
    if (getProjectSuccess) {
      showToasts(
        SUCCESS_KEY,
        getSuccessMessage(getProjectSuccess as SuccessCodes),
        'success',
      );
      dispatch(resetMessage());
    }
  }, [getProjectSuccess, dispatch, showToasts]);

  // handlers
  function gotoProject(proj_id: number,name:string) {
    dispatch(setCurrentSelectedProject(proj_id));
    dispatch(setProjectName(name));
    navigation.navigate(Routes.PROJECTDASHBOARD, {id: proj_id.toString()});
  }

  function addProject() {
    navigation.navigate(Routes.ADDPROJECT);
  }

  function handleSort(field: keyof typeof sort) {
    setSort(prev => {
      let newSort = structuredClone(prev);
      if (newSort[field]) {
        newSort[field] = newSort[field] === 'asc' ? 'desc' : 'asc';
      } else {
        newSort = {[field]: 'asc'};
      }
      return newSort;
    });
  }

  function goBack() {
    navigation.navigate(Routes.DASHBOARD);
  }

  const columns: Column[] = [
    {
      key: 'proj_id',
      label: (
        <Pressable
          style={styles.tableCellWrapper}
          onPress={() => handleSort('id')}>
          <Text style={styles.tableHeader}>ID</Text>
          <Image
            resizeMode="contain"
            style={styles.tableHeaderIcon}
            source={require('assets/icons/swap_vert@2x.png')}
          />
        </Pressable>
      ),
      renderCell: (cellData: Project) => (
        <Text style={styles.linkCellText}>{cellData.id}</Text>
      ),
      minWidth: 40,
    },
    {
      key: 'name',
      label: (
        <Pressable
          style={styles.tableCellWrapper}
          onPress={() => handleSort('name')}>
          <Text style={styles.tableHeader}>Project Title</Text>
          <Image
            resizeMode="contain"
            style={styles.tableHeaderIcon}
            source={require('assets/icons/swap_vert@2x.png')}
          />
        </Pressable>
      ),
      renderCell: (proj: Project) => (
        <Pressable onPress={() => gotoProject(proj.id,proj.name)}>
          <Text style={styles.linkCellText}>{proj.name}</Text>
        </Pressable>
      ),
    },
    {
      key: 'start_date',
      label: 'Start Date',
      renderCell: (cellData: Project) => (
        <Text style={!cellData?.start_date ? styles.linkCellTextNoUnderline : null}>
          {cellData?.start_date ? getFormattedDate(new Date(cellData.start_date)) : '--'}
        </Text>
      ),
    },
    {
      key: 'end_date',
      label: 'End Date',
      renderCell: (cellData: Project) => (
        <Text style={!cellData?.start_date ? styles.linkCellTextNoUnderline : null}>
          {cellData?.end_date ? getFormattedDate(new Date(cellData.end_date)) : '--'}
        </Text>
      ),
    },
    {
      key: 'status',
      label: (
        <Pressable
          style={styles.tableCellWrapper}
          onPress={() => setStatusDropDownOpen(prev => !prev)}>
          <Text style={styles.tableHeader}>Status</Text>
          <Image
            resizeMode="contain"
            style={styles.tableHeaderIcon}
            source={require('assets/icons/cheveron_down@2x.png')}
          />
          {statusDropDownOpen && (
            <SimpleDropdown
              options={Object.values(projectStatusEnum)}
              onSelect={(value: string) => {
                const sortValue = Object.entries(projectStatusEnum)?.find(
                  ([_, v]) => v === value,
                )![0] as any;
                setFilterState(sortValue);
                setStatusDropDownOpen(false);
              }}
              style={styles.statusWrapper}
            />
          )}
        </Pressable>
      ),
      renderCell: (cellData: Project) => (
        <StatusTag
          variant={projectStatusEnum[cellData.status]}
          text={projectStatusEnum[cellData.status]}
        />
      ),
    },
  ];

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.heading}>All Projects</Text>
        <Icon size={28} type="back_arrow" onPress={goBack} />
      </View>
      <View style={styles.header}>
        {/* search bar */}
        <View style={styles.searchWrapper}>
          <Image
            style={styles.icon}
            source={require('assets/icons/search@2x.png')}
          />
          <TextInput
            style={styles.searchInput}
            onChangeText={setSearchText}
            placeholder="Search Project by name or id"
            placeholderTextColor={colors.palette.nuetral4Placeholder}
          />
        </View>

        <View style={styles.headerAction}>
          {/* <ActionIconButton
            onPress={fetchProjects}
            icon={require('assets/icons/retry@2x.png')}
          /> */}
          {/* add employee button */}
          {hasWriteAccess ? (
            <Button
              onPress={addProject}
              text="Add Project"
              style={styles.addButton}
            />
          ) : null}
        </View>
      </View>
      <DisplayTableContent
        columns={columns}
        data={projects}
        isLoading={isLoading}
        pageHandler={setCurrentPage}
        currentPage={currentPage}
        totalPages={totalPages}
        keyExtractor={item => item.id.toString()}
      />
    </View>
  );
}

type displayContentProps = Readonly<{
  isLoading: boolean;
  columns: Column[];
  data: Project[];
  totalPages: number;
  currentPage: number;
  keyExtractor: (item: Project) => string;
  pageHandler: (page: number) => void;
}>;
function DisplayTableContent(props: displayContentProps) {
  const {
    columns,
    currentPage,
    isLoading,
    pageHandler,
    totalPages,
    keyExtractor,
    data,
  } = props;
  if (isLoading) {
    return <Text>Please wait while we load your data</Text>;
  }
  return (
    <View style={styles.tableWrapper}>
      <Table
        columns={columns}
        data={data}
        keyExtractor={keyExtractor}
        pageHandler={pageHandler}
        currentPage={currentPage}
        totalPages={totalPages}
      />
    </View>
  );
}

type styleType = {
  container: ViewStyle;
  header: ViewStyle;
  searchWrapper: ViewStyle;
  icon: ImageStyle;
  searchInput: TextStyle;
  addButton: ViewStyle;
  heading: TextStyle;
  linkCellText: TextStyle;
  linkCellTextNoUnderline: TextStyle;
  tableHeader: TextStyle;
  tableHeaderIcon: ImageStyle;
  tableCellWrapper: ViewStyle;
  filterWrapper: ViewStyle;
  tableWrapper: ViewStyle;
  statusWrapper: ViewStyle;
  calendar: ViewStyle;
  headerAction: ViewStyle;
};

const styles: styleType = {
  statusWrapper: {
    top: 40,
    left: 0,
    maxWidth: 200,
    position: 'absolute',
  },
  headerAction: {
    flexDirection: 'row',
    gap: spacing['2'],
  },
  calendar: {
    top: 0,
    left: 0,
    minHeight: 350,
  },
  tableWrapper: {zIndex: -1},
  filterWrapper: {
    position: 'relative',
  },
  tableCellWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing['1'],
  },
  tableHeaderIcon: {
    width: 16,
    height: 16,
    aspectRatio: '1/1',
  },
  tableHeader: {
    fontSize: 18,
    fontWeight: 500,
  },
  linkCellText: {textDecoration: 'underline'},
  linkCellTextNoUnderline: {marginLeft: spacing['8']},
  heading: {
    color: colors.palette.brandBlue,
    fontSize: 32,
    fontWeight: 600,
  },
  container: {
    padding: spacing['4'],
    gap: spacing['4'],
    minHeight: '100%',
  },
  header: {
    width: '100%',
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  icon: {
    height: 16,
    width: 16,
  },
  searchWrapper: {
    overflow: 'hidden',
    flexDirection: 'row',
    paddingHorizontal: spacing['2'],
    alignItems: 'center',
    borderRadius: corner_radius['rounded-md'],
    maxWidth: 400,
    backgroundColor: colors.palette.gray,
    minWidth: 0,
    width: 400,
    gap: spacing['2'],
  },
  addButton: {
    maxWidth: 150,
    minWidth: 0,
    marginLeft: 'auto',
  },
  searchInput: {
    paddingVertical: spacing['2'],
    width: '100%',
    outlineWidth: 0,
    outlineColor: colors.palette.gray,
  },
};
